import * as _ from 'lodash';
import * as React from 'react';
import * as Validator from 'validator';
import * as Model from '../../api/model';
import * as Request from '../../api/request';
import { WithStyles, Theme, TableRow, TableCell, Tooltip, IconButton, CircularProgress, Snackbar, FormControl, Input, FormHelperText, Select, MenuItem } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import withStyles, { CSSProperties } from '@material-ui/core/styles/withStyles';
import { API } from '../../api';

//#region UI configuration
const strings = {
    saveButton: 'Save',
    cancelButton: 'Cancel',
    addError: 'Unable to add, try again',
    namePlaceholder: 'Enter name',
    nameIsRequired: 'Name is required',
    parentIsRequired: 'Continent is required',
};

const styles = (theme: Theme) => ({
    formControl: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center'
    } as CSSProperties,
    formField: {
        width: 300,
        marginRight: 8
    } as CSSProperties
});

type Styles = WithStyles<'formControl' | 'formField'>;
//#endregion

//#region Form configuration
const enum FormFieldNames {
    NAME = 'name',
    CONTINENT = 'continent'
}

interface FormFields {
    name: string;
    continent: { id: number, value: string } | null;
}

interface FormErrors {
    name?: string;
    continent?: string;
}
//#endregion

//#region Props & State
interface CountryAddItemProps {
    onItemAdded: (model: Model.Country) => void;
    onCancelItemAdd: () => void;
}
interface CountryAddItemState {
    saving: boolean;
    loading: boolean;
    error: null | string;
    fields: FormFields;
    errors: FormErrors;
    parents: Model.Continent[];
    showParents: boolean;
}

type PropsWithStyles = CountryAddItemProps & Styles;

const initialState: CountryAddItemState = {
    saving: false,
    loading: false,
    error: null,
    fields: {
        name: '',
        continent: null
    },
    errors: {},
    parents: [],
    showParents: false
};
//#endregion

class CountryAddItem extends React.Component<PropsWithStyles, CountryAddItemState> {

    private nameInput: HTMLInputElement;

    constructor(props: PropsWithStyles) {
        super(props);

        this.state = initialState;
    }

    render() {
        const { classes, onCancelItemAdd } = this.props;
        const { saving, error, errors, fields, parents, loading, showParents } = this.state;

        return (
            <React.Fragment>
                <TableRow selected={true}>
                    <TableCell numeric={true}>
                        {!saving && <React.Fragment>
                            <Tooltip title={strings.cancelButton}>
                                <IconButton onClick={onCancelItemAdd}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={strings.saveButton}>
                                <IconButton onClick={this.onSaveClick}>
                                    <SaveIcon />
                                </IconButton>
                            </Tooltip>
                        </React.Fragment>}
                        {saving && <CircularProgress size={45} />}
                    </TableCell>
                    <TableCell>
                        <FormControl margin="none" className={classes.formControl} error={!!errors.name}>
                            <Input
                                autoFocus={true}
                                className={classes.formField}
                                id={FormFieldNames.NAME}
                                inputRef={ref => this.nameInput = ref}
                                name={FormFieldNames.NAME}
                                placeholder={strings.namePlaceholder}
                                onChange={this.onFieldChange}
                                type="text"
                                value={fields.name}
                            />
                            <FormHelperText>{errors.name}</FormHelperText>
                        </FormControl>
                    </TableCell>
                    <TableCell>
                        <FormControl margin="none" className={classes.formControl} error={!!errors.continent}>
                            <Select
                                className={classes.formField}
                                value={fields.continent ? fields.continent.id : 0}
                                onChange={this.onParentSelectChange}
                                displayEmpty={true}
                                onOpen={this.onParentSelectOpen}
                                onClose={this.onParentSelectClose}
                                open={showParents}
                                renderValue={value => (<span>{fields.continent ? fields.continent.value : ''}</span>)}
                                inputProps={{
                                    name: FormFieldNames.CONTINENT,
                                    id: FormFieldNames.CONTINENT,
                                }}
                            >
                                {parents.map(parent => (
                                    <MenuItem key={parent.id} value={parent.id}>{parent.name}</MenuItem>
                                ))}
                            </Select>
                            {loading && <CircularProgress size={24} />}
                            <FormHelperText>{errors.continent}</FormHelperText>
                        </FormControl>
                    </TableCell>
                    <TableCell />
                </TableRow>

                <Snackbar
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    open={!!error}
                    autoHideDuration={6000}
                    onClose={this.onErrorClose}
                    message={<span>{error}</span>}
                    action={[
                        <IconButton
                            key="close"
                            color="inherit"
                            onClick={this.onErrorClose}
                        >
                            <CloseIcon />
                        </IconButton>
                    ]}
                />
            </React.Fragment>
        );
    }

    private onErrorClose = () => {
        this.setState({ error: null });
    }

    private onFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({
            fields: { ...this.state.fields, [event.target.name]: event.target.value }
        });
    }

    private onSaveClick = () => {
        const errors: FormErrors = this.validateFormFields(this.state.fields);
        this.setState({ errors });

        if (_.isEmpty(errors) && this.state.fields.continent) {
            const { onItemAdded } = this.props;
            const { name, continent } = this.state.fields;

            this.setState({ saving: true });

            API.country.add(new Request.Country(continent.id, name))
                .then(model => {
                    onItemAdded(model);
                })
                .catch(() => {
                    this.setState({ saving: false, error: strings.addError });
                });
        } else if (!!errors.name) {
            this.nameInput.focus();
        }
    }

    private validateFormFields(fields: FormFields): FormErrors {
        const errors: FormErrors = {};

        if (Validator.isEmpty(fields.name)) {
            errors.name = strings.nameIsRequired;
        }

        if (!fields.continent || fields.continent.id <= 0) {
            errors.continent = strings.parentIsRequired;
        }

        return errors;
    }

    private onParentSelectOpen = () => {
        const { parents, loading } = this.state;
        if (parents.length === 0 && !loading) {
            this.setState({ loading: true });
            API.continent.list().then(items => {
                this.setState({ loading: false, parents: items, showParents: true });
            });
        } else {
            this.setState({ showParents: true });
        }
    }

    private onParentSelectClose = () => {
        this.setState({ showParents: false });
    }

    private onParentSelectChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const { parents } = this.state;
        const selectedId = parseInt(event.target.value, 10);
        const selected = _.find(parents, (o) => o.id === selectedId);
        if (selected) {
            this.setState({
                fields: { ...this.state.fields, [event.target.name]: { id: selected.id, value: selected.name } }
            });
        }
    }
}

export default withStyles(styles)<CountryAddItemProps>(CountryAddItem);



// WEBPACK FOOTER //
// ./src/modules/country/CountryAddItem.tsx